import type { GlobalFilter, QueryFormatFilter } from '@hotjar/global-filters';
import type { QuantzProperty, QuantzOperation, QuantzLevel, QuantzEventType } from '@hotjar/quantz';
import type { SiteId } from '../site/model';

export type ActionKind = 'heatmapLink' | 'recordingLink';

export interface BasicWidgetConfig<T extends WidgetTemplate> {
  id: WidgetId; // If it's a saved widget then it's the backendId else it's a random id
  backendId?: WidgetId;
  dashboardId?: DashboardId;
  kind: WidgetKind;
  template: T;
  title: string;
  subtitle?: string;
}

export interface QueryInfo {
  groupBy?: QuantzProperty;
  limit?: number;
  windowed?: boolean;
  eventFilters?: QueryFormatFilter;
  eventType?: QuantzEventType;
  property?: QuantzProperty;
  operation: QuantzOperation;
  level?: QuantzLevel;
  sessionFilters?: GlobalFilter;
}

export interface WithBasicQueryInfo {
  queryInfo: QueryInfo[];
}

export interface WithGroupByOptions {
  groupByOptions?: QuantzProperty[];
}

export interface WithRenderInfo {
  renderInfo: {
    calculationType: CalculationType;
    chartType: ChartType;
  };
}

export interface WidgetConfigGenericMetric
  extends BasicWidgetConfig<
      | WidgetTemplate.UturnRageClickWidget
      | WidgetTemplate.IsFirstSeenWidget
      | WidgetTemplate.TechnologyWidget
      | WidgetTemplate.CountryWidget
      | WidgetTemplate.TopPageWidget
      | WidgetTemplate.TopClickedWidget
      | WidgetTemplate.TrendWidget
      | WidgetTemplate.SessionsWithErrorWidget
    >,
    WithBasicQueryInfo,
    WithRenderInfo,
    WithGroupByOptions {}

export interface WidgetConfigFeedbackMetric
  extends BasicWidgetConfig<WidgetTemplate.FeedbackWidget>,
    WithBasicQueryInfo {}

export interface WidgetConfigSessionsInfoMetric
  extends BasicWidgetConfig<
      | WidgetTemplate.TotalSessionsWidget
      | WidgetTemplate.BounceRateWidget
      | WidgetTemplate.AverageSessionDurationWidget
      | WidgetTemplate.AveragePagesPerSessionWidget
    >,
    WithBasicQueryInfo {}

export type WidgetConfig =
  | WidgetConfigGenericMetric
  | WidgetConfigFeedbackMetric
  | WidgetConfigSessionsInfoMetric;

export enum WidgetTemplate {
  TotalSessionsWidget = 'totalSessionsWidget',
  AverageSessionDurationWidget = 'averageSessionDurationWidget',
  AveragePagesPerSessionWidget = 'averagePagesPerSessionWidget',
  BounceRateWidget = 'bounceRateWidget',
  TopPageWidget = 'topPageWidget',
  FeedbackWidget = 'feedbackWidget',
  UturnRageClickWidget = 'uturnRageClickWidget',
  IsFirstSeenWidget = 'isFirstSeenWidget',
  TechnologyWidget = 'technologyWidget',
  CountryWidget = 'countryWidget',
  TopClickedWidget = 'topClickedWidget',
  TrendWidget = 'trendWidget',
  SessionsWithErrorWidget = 'sessionsWithErrorWidget',
}

export enum CalculationType {
  percentage = 'percentage',
  raw = 'raw',
}

export enum ChartType {
  pie = 'pie',
  timeline = 'timeline',
  bar = 'bar',
  number = 'number',
}

export enum RequestType {
  put = 'put',
  post = 'post',
}

export interface DashboardConfig {
  id: DashboardId;
}

export interface SaveDashboardRequest {
  siteId: SiteId;
  dashboardId?: DashboardId;
  widgets: WidgetConfig[];
  requestType: RequestType;
}

export interface GetDashboardWidgetsRequest {
  siteId: SiteId;
  dashboardId: DashboardId;
}

export interface GetDashboardsConfigRequest {
  siteId: SiteId;
}

export interface DashboardWidgetsResponse {
  widgets: WidgetConfig[];
}

export interface DashboardsResponse {
  dashboards: DashboardConfig[];
}

export interface DeleteWidgetConfigRequest {
  siteId: SiteId;
  dashboardId: DashboardId;
  widgetId: string;
}

export interface CreateWidgetConfigRequest {
  siteId: SiteId;
  dashboardId: DashboardId;
  widget: WidgetConfig;
}

export type WidgetId = string;

export type DashboardId = string;

export enum WidgetKind {
  default = 'default',
  trend = 'trend',
}
